import { PageHeader } from '@my/ui';
import { MapPin, Network, Smartphone, UsersRound, type LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { ErrorBanner } from '@my/ui';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { MobileUsersTab } from './MobileUsersTab';
import { SitesTab } from './SitesTab';
import { TeamsTab } from './TeamsTab';
import { UsersTab } from './UsersTab';

/**
 * Settings → Users & Access page. Tabbed home for everything user/access
 * management beyond the small Administrators card on the Organisation
 * page. Four tabs:
 *
 *   - **Users** — web (Auth0) users with a Check grant on this org, incl.
 *     the functional-roles multi-select. Live.
 *   - **Teams** — sub-site working groups. Live (org-wide memberships).
 *   - **Sites** — UserSite assignments. Live (org-wide memberships).
 *   - **Mobile Users** — phone-authenticated field identities (drivers /
 *     operatives) that use the Check mobile app. Live.
 *
 * Active tab is reflected in `?tab=` so a refresh / share-link preserves
 * state. Default tab is `users`.
 */
type TabId = 'users' | 'teams' | 'sites' | 'mobile';

const VALID_TABS: TabId[] = ['users', 'teams', 'sites', 'mobile'];

function isTabId(value: string): value is TabId {
  return (VALID_TABS as string[]).includes(value);
}

export function UsersAndAccessPage() {
  const { t } = useTranslation('organisation');
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') ?? '';
  const activeTab: TabId = isTabId(rawTab) ? rawTab : 'users';

  const setTab = (id: TabId) => {
    // `replace: true` so tab swaps don't pollute browser history — a
    // back-button click should leave the page entirely, not undo a tab
    // change. Same convention used by other tabbed surfaces in the suite.
    setSearchParams({ tab: id }, { replace: true });
  };

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title={t('usersAndAccess.title')}
        description={t('usersAndAccess.description')}
      />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title={t('common:errors.noOrgLinkedTitle', {
              defaultValue: 'No organisation linked to this account',
            })}
            message={t('common:errors.noOrgLinkedMessage', {
              defaultValue:
                'Your access token does not include a Check organisation claim. Contact an administrator.',
            })}
          />
        )}

        {orgId && (
          // Mobile: flex column — sub-nav at top (natural height), content
          // below. Avoids the CSS-grid row-stretching quirk that bites
          // when one tab's content is wider than the viewport (the Users
          // table forces `min-w-[1080px]` for horizontal scroll). Grid
          // only kicks in at md+ where the 200px sidebar matters.
          <div className="flex min-h-0 flex-1 flex-col gap-3 md:grid md:grid-cols-[200px_1fr]">
            <SubNav activeTab={activeTab} onChange={setTab} />
            <div className="flex min-h-0 flex-1 flex-col">
              {activeTab === 'users' && <UsersTab organisationId={orgId} />}
              {activeTab === 'teams' && <TeamsTab organisationId={orgId} />}
              {activeTab === 'sites' && <SitesTab organisationId={orgId} />}
              {activeTab === 'mobile' && <MobileUsersTab organisationId={orgId} />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

interface SubNavProps {
  activeTab: TabId;
  onChange: (id: TabId) => void;
}

/**
 * Responsive sub-nav for the Users & Access page. No surrounding card —
 * items sit on the page background and the only chrome is a single
 * separator line between the nav and the content panel:
 *
 *   - **md and up**: vertical list in a 200px column with a 1px
 *     `border-r` between the nav and the content. Active item gets a
 *     left accent bar + tinted purple background + bold purple text.
 *   - **below md**: horizontal pill row above the panel with a 1px
 *     `border-b` separator. Active styling is the same minus the left
 *     accent (would read as an arbitrary line on a horizontal pill).
 *     Overflows scroll horizontally if more sections land later.
 *
 * Icons:
 *   - Users → `UsersRound` (rounded multi-silhouette)
 *   - Teams → `Network` (connected nodes — "structure of groups")
 *   - Sites → `MapPin` (matches Site usage elsewhere in the app)
 */
const TAB_ICONS: Record<TabId, LucideIcon> = {
  users: UsersRound,
  teams: Network,
  sites: MapPin,
  mobile: Smartphone,
};

function SubNav({ activeTab, onChange }: SubNavProps) {
  const { t } = useTranslation('organisation');
  return (
    <nav
      role="tablist"
      aria-label={t('usersAndAccess.title')}
      className="flex items-start gap-2 overflow-x-auto border-b border-slate-200 pb-2 md:flex-col md:items-stretch md:overflow-visible md:border-b-0 md:border-r md:pb-0 md:pr-2 md:pt-2"
    >
      {VALID_TABS.map((id) => {
        const active = id === activeTab;
        const Icon = TAB_ICONS[id];
        return (
          <button
            key={id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(id)}
            className={
              'flex shrink-0 items-center gap-2 rounded-sm px-2.5 py-2 text-sm transition-colors md:border-l-2 ' +
              (active
                ? 'bg-purple-50 font-medium text-purple-700 md:border-purple-600'
                : 'text-slate-600 hover:bg-slate-50 md:border-transparent')
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {t(`usersAndAccess.tabs.${id}`, { defaultValue: id })}
          </button>
        );
      })}
    </nav>
  );
}
